import { motion } from "framer-motion";

const HERO_VIDEO =
  "https://res.cloudinary.com/jqvef0es/video/upload/v1783186162/portada_portfolio_asombro_ijtzos.mp4";

const MARQUEE_TEXT = "CONTENIDO VISUAL PARA PROVOCAR ASOMBRO";

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative w-full pt-20 md:pt-24"
    >
      {/* Top meta bar */}
      <div className="border-b border-divider">
        <div className="mx-auto flex items-center justify-between px-6 md:px-10 py-3 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-white/60">
          <span data-testid="hero-meta-left">Asunción · Paraguay</span>
          <span className="hidden md:block">Productora Audiovisual · Est. 2025</span>
          <span data-testid="hero-meta-right">[Vol.01 / MMXXV]</span>
        </div>
      </div>

      {/* Marquee - brand color, above hero video. Seamless infinite loop. */}
      <div
        data-testid="hero-marquee"
        className="relative overflow-hidden border-b border-divider bg-black py-6 md:py-8"
      >
        <div className="ticker-track flex items-center gap-8 md:gap-14 whitespace-nowrap font-display uppercase text-rust tracking-[-0.03em] leading-[0.9] text-[9vw] md:text-[6.5vw] lg:text-[5.6vw] w-max">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={`mq-${i}`}
              className="flex items-center gap-8 md:gap-14 shrink-0"
            >
              <span>{MARQUEE_TEXT}</span>
              <span
                aria-hidden="true"
                className="inline-block w-4 h-4 md:w-6 md:h-6 rounded-full bg-rust shrink-0"
              />
            </span>
          ))}
        </div>
      </div>

      {/* Full-width hero video */}
      <div className="relative w-full overflow-hidden bg-black border-b border-divider">
        <div className="relative w-full aspect-[16/9] md:aspect-[21/9] lg:aspect-[21/9] max-h-[86vh]">
          <video
            data-testid="hero-video"
            src={HERO_VIDEO}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Subtle vignette for legibility */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/70 via-black/10 to-black/40" />

          {/* Corner markers */}
          <div className="absolute top-4 md:top-6 left-4 md:left-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/80 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-rust animate-pulse" />
            REC · LOOP · Portada
          </div>
          <div className="absolute top-4 md:top-6 right-4 md:right-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
            AS/HERO-001
          </div>
          <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
            Asombro Films · 2025
          </div>
          <div className="absolute bottom-4 md:bottom-6 right-4 md:right-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
            9:16 · 4K · H264
          </div>
        </div>
      </div>

      {/* Manifesto row below the video */}
      <div className="grid grid-cols-12 border-b border-divider">
        <div className="col-span-12 md:col-span-3 border-b md:border-b-0 md:border-r border-divider px-6 md:px-10 py-8 md:py-14">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust mb-4"
            data-testid="hero-eyebrow"
          >
            [01] Manifiesto
          </motion.div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 leading-relaxed max-w-[22ch]">
            Una productora audiovisual que trabaja donde la marca, la historia y la mirada se encuentran.
          </div>
        </div>
        <div className="col-span-12 md:col-span-9 px-6 md:px-10 py-8 md:py-14">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100, damping: 15, delay: 0.1 }}
            data-testid="hero-manifesto"
            className="font-serif text-2xl md:text-4xl lg:text-5xl leading-[1.2] tracking-[-0.01em] text-white max-w-[36ch]"
          >
            En <span className="text-rust">Asombro</span> creemos que una imagen puede hacer más que mostrar.
            Puede detener la mirada, despertar una emoción y transformar la forma en que
            una historia es recordada.
          </motion.p>
        </div>
      </div>

      {/* Services ticker */}
      <div className="overflow-hidden border-b border-divider bg-black">
        <div className="flex ticker-track whitespace-nowrap py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-white/60">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0">
              {[
                "Dirección Creativa",
                "Producción Audiovisual",
                "Reels 9:16",
                "Branded Content",
                "Documental",
                "Dirección de Fotografía",
                "Post-producción",
              ].map((item, i) => (
                <span key={`${k}-${i}`} className="mx-8 flex items-center gap-8">
                  {item}
                  <span className="text-rust">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
